import { Component, OnInit } from '@angular/core';
import { DataService } from '../../../shared/services/data.service';
import * as Highcharts from 'highcharts';

interface ExecutionKpiMetric {
  exeHistoryId: number;
  exeHeaderId: number;
  testSuiteId: number;
  brandName: string;
  platformName: string;
  buildNo: string;
  testType: string;
  metricName: string;
  metricParameter: string;
  aggregationType: string;
  expectedResult: string;
  actualResult: string;
  unit: string;
  sampleCount: number;
  status: string;
  projectName?: string;
  createdOn: string;
  modifiedOn?: string;
  metrics?: Array<{
    metricName: string;
    metricParameter: string;
    aggregationType: string;
    expectedResult: string;
    actualResult: string;
    unit: string;
    sampleCount: number;
    status: string;
  }>;
}

interface KpiGaugeCard {
  label: string;
  metricName: string;
  startValue: number | null;
  endValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  avgValue: number | null;
  displayStats: Array<{ label: string; value: number | null }>;
  arcLeftLabel: string;
  arcLeftValue: number | null;
  arcRightLabel: string;
  arcRightValue: number | null;
  expectedResult: string;
  unit: string;
  sampleCount: number;
  status: string;
  score: number;
  createdOn: string;
}

interface TrendSeriesDefinition {
  key: string;
  label: string;
  unit: string;
  color: string;
  data: Array<number | null>;
}

interface TrendTableRow {
  parameter: string;
  latest: string;
  min: string;
  max: string;
}

@Component({
  selector: 'wtaf-app-kpi-dashboard-widget',
  templateUrl: './app-kpi-dashboard-widget.component.html',
  styleUrls: ['./app-kpi-dashboard-widget.component.css']
})
export class AppKpiDashboardWidgetComponent implements OnInit {
  readonly gaugeRadius = 54;
  readonly gaugeStrokeWidth = 12;
  readonly gaugeSemiCircumference = Math.PI * this.gaugeRadius;

  loading = false;
  errorMessage = '';
  viewMode: 'gauge' | 'trend' = 'gauge';

  rawMetrics: ExecutionKpiMetric[] = [];
  gaugeCards: KpiGaugeCard[] = [];
  trendMetricOptions: string[] = [];
  selectedTrendMetric = '';
  trendCategories: string[] = [];
  trendSeries: TrendSeriesDefinition[] = [];
  trendTableRows: TrendTableRow[] = [];
  trendChartOptions: any = {};
  updateTrendChart = false;
  highcharts: any = Highcharts;

  brandOptions: string[] = [];
  projectOptions: string[] = [];
  buildOptions: string[] = [];
  platformOptions: string[] = [];

  selectedBrandName = '';
  selectedProjectName = '';
  selectedBuildNo = '';
  selectedPlatformName = '';

  summary = {
    total: 0,
    pass: 0,
    fail: 0,
    unknown: 0,
    lastUpdated: ''
  };

  constructor(private dataservice: DataService) {}

  ngOnInit() {
    this.loadMetrics();
  }

  refresh() {
    this.loadMetrics(true);
  }

  setViewMode(mode: 'gauge' | 'trend') {
    this.viewMode = mode;
    if (mode === 'trend') {
      this.buildTrendData();
    }
  }

  onBrandChange() {
    this.updateProjectOptions();
    this.updateBuildOptions();
    this.selectedPlatformName = '';
    this.updatePlatformOptions();
    this.buildKpiCards();
    this.buildTrendData();
  }

  onProjectChange() {
    this.updateBuildOptions();
    this.selectedPlatformName = '';
    this.updatePlatformOptions();
    this.buildKpiCards();
    this.buildTrendData();
  }

  onBuildChange() {
    this.selectedPlatformName = '';
    this.updatePlatformOptions();
    this.buildKpiCards();
    this.buildTrendData();
  }

  onPlatformChange() {
    this.buildKpiCards();
    this.buildTrendData();
  }

  onTrendMetricChange() {
    this.buildTrendData();
  }

  getGaugeColor(status: string): string {
    const normalizedStatus = (status || '').toUpperCase();
    if (normalizedStatus === 'PASS') {
      return '#1aaa55';
    }

    if (normalizedStatus === 'FAIL') {
      return '#db3b21';
    }

    return '#6b7280';
  }

  getStatusClass(status: string): string {
    const normalizedStatus = (status || '').toUpperCase();
    if (normalizedStatus === 'PASS') {
      return 'status-pass';
    }

    if (normalizedStatus === 'FAIL') {
      return 'status-fail';
    }

    return 'status-unknown';
  }

  private loadMetrics(forceRefresh = false) {
    this.loading = true;
    this.errorMessage = '';

    this.dataservice
      .getExecutionKpiMetrics(
        undefined,
        undefined,
        undefined,
        'Performance',
        forceRefresh
      )
      .subscribe(
      (response: any) => {
        this.rawMetrics = this.extractRows(response);
        this.initializeFilters();
        this.buildKpiCards();
        this.buildTrendData();
        this.loading = false;
      },
      () => {
        this.rawMetrics = [];
        this.gaugeCards = [];
        this.clearTrendData();
        this.loading = false;
        this.errorMessage =
          'Unable to fetch KPI metrics. Please try again after some time.';
      }
    );
  }

  private extractRows(response: any): ExecutionKpiMetric[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return this.flattenExecutionRows(response);
    }

    if (Array.isArray(response.listOfRecords)) {
      return this.flattenExecutionRows(response.listOfRecords);
    }

    return [];
  }

  private flattenExecutionRows(rows: any[]): ExecutionKpiMetric[] {
    const flattened: ExecutionKpiMetric[] = [];

    rows.forEach(item => {
      if (item && Array.isArray(item.metrics) && item.metrics.length > 0) {
        item.metrics.forEach(metric => {
          flattened.push({
            exeHistoryId: item.exeHistoryId,
            exeHeaderId: item.exeHeaderId,
            testSuiteId: item.testSuiteId,
            brandName: item.brandName,
            platformName: item.platformName,
            buildNo: item.buildNo,
            testType: item.testType,
            metricName: metric.metricName,
            metricParameter: metric.metricParameter,
            aggregationType: metric.aggregationType,
            expectedResult: metric.expectedResult,
            actualResult: metric.actualResult,
            unit: metric.unit,
            sampleCount: metric.sampleCount,
            status: metric.status,
            projectName: item.projectName,
            createdOn: item.createdOn,
            modifiedOn: item.modifiedOn
          });
        });
        return;
      }

      flattened.push(item as ExecutionKpiMetric);
    });

    return flattened;
  }

  private initializeFilters() {
    this.brandOptions = this.getUniqueValues(this.rawMetrics, 'brandName');

    if (
      this.selectedBrandName === '' ||
      this.brandOptions.indexOf(this.selectedBrandName) === -1
    ) {
      this.selectedBrandName = this.brandOptions.length
        ? this.brandOptions[0]
        : '';
    }

    this.updateProjectOptions();
    this.updateBuildOptions();
    this.updatePlatformOptions();
  }

  private updateProjectOptions() {
    const rows = this.rawMetrics.filter(
      row => row.brandName === this.selectedBrandName
    );

    this.projectOptions = this.getUniqueValues(rows, 'projectName');

    if (
      this.selectedProjectName === '' ||
      this.projectOptions.indexOf(this.selectedProjectName) === -1
    ) {
      this.selectedProjectName = this.projectOptions.length
        ? this.projectOptions[0]
        : '';
    }
  }

  private updateBuildOptions() {
    const rows = this.rawMetrics.filter(
      row =>
        row.brandName === this.selectedBrandName &&
        (!this.selectedProjectName || row.projectName === this.selectedProjectName)
    );

    const latestDateByBuild: { [buildNo: string]: number } = {};

    rows.forEach(row => {
      const buildNo = row.buildNo || '';
      if (!buildNo) {
        return;
      }

      const createdTime = this.getCreatedTime(row.createdOn);
      if (
        latestDateByBuild[buildNo] === undefined ||
        createdTime > latestDateByBuild[buildNo]
      ) {
        latestDateByBuild[buildNo] = createdTime;
      }
    });

    this.buildOptions = Object.keys(latestDateByBuild).sort(
      (firstBuild, secondBuild) =>
        latestDateByBuild[secondBuild] - latestDateByBuild[firstBuild]
    );

    if (
      this.selectedBuildNo === '' ||
      this.buildOptions.indexOf(this.selectedBuildNo) === -1
    ) {
      this.selectedBuildNo = this.buildOptions.length ? this.buildOptions[0] : '';
    }
  }

  private updatePlatformOptions() {
    const rows = this.rawMetrics.filter(
      row =>
        row.brandName === this.selectedBrandName &&
        (!this.selectedProjectName || row.projectName === this.selectedProjectName) &&
        row.buildNo === this.selectedBuildNo
    );
    this.platformOptions = this.getUniqueValues(rows, 'platformName');
  }

  private buildKpiCards() {
    const filteredRows = this.rawMetrics.filter(
      row =>
        row.brandName === this.selectedBrandName &&
        (!this.selectedProjectName || row.projectName === this.selectedProjectName) &&
        row.buildNo === this.selectedBuildNo &&
        (!this.selectedPlatformName ||
          row.platformName === this.selectedPlatformName)
    );

    const metricRowsMap = new Map<string, ExecutionKpiMetric[]>();

    filteredRows.forEach(row => {
      const metricKey = (row.metricName || '').trim();
      if (!metricRowsMap.has(metricKey)) {
        metricRowsMap.set(metricKey, []);
      }
      metricRowsMap.get(metricKey).push(row);
    });

    this.gaugeCards = Array.from(metricRowsMap.entries())
      .sort((left, right) => left[0].localeCompare(right[0]))
      .map(([metricName, rows]) => {
        const selectedRows = this.getLatestRowsByParameterAndAggregation(rows);
        const numericRows = this.getNumericRows(selectedRows);

        const startRow =
          this.pickRowByParameters(selectedRows, ['START', 'BEGIN']) ||
          this.pickRowByAggregations(selectedRows, ['FIRST']);
        const endRow =
          this.pickRowByParameters(selectedRows, ['END']) ||
          this.pickRowByAggregations(selectedRows, ['LAST']);
        const minRow = this.pickRowByAggregations(selectedRows, ['MIN']);
        const maxRow =
          this.pickRowByAggregations(selectedRows, ['MAX']) ||
          this.pickRowByParameters(selectedRows, ['PEAK']);
        const avgRow = this.pickPreferredAvgRow(selectedRows);

        const startValue = startRow ? this.parseNumber(startRow.actualResult) : null;
        const endValue = endRow ? this.parseNumber(endRow.actualResult) : null;
        const minValue = minRow ? this.parseNumber(minRow.actualResult) : null;
        const maxValue = maxRow ? this.parseNumber(maxRow.actualResult) : null;
        const avgValue = avgRow
          ? this.parseNumber(avgRow.actualResult)
          : this.getAverageValueFromRows(numericRows);
        const fallbackMaxFromExpected = this.parseNumber(
          this.getExpectedResult(avgRow, selectedRows)
        );

        let rangeMinValue = minValue;
        let rangeMaxValue = maxValue;

        if (
          (rangeMinValue === null || rangeMaxValue === null) &&
          startValue !== null &&
          endValue !== null
        ) {
          rangeMinValue = Math.min(startValue, endValue);
          rangeMaxValue = Math.max(startValue, endValue);
        }

        if (rangeMinValue === null) {
          rangeMinValue = this.getMinValueFromRows(numericRows);
        }

        if (rangeMaxValue === null) {
          rangeMaxValue = this.getMaxValueFromRows(numericRows) || fallbackMaxFromExpected;
        }

        const avgForScore =
          avgValue !== null
            ? avgValue
            : endValue !== null
            ? endValue
            : this.getAverageValueFromRows(numericRows);

        const hasMinMaxSemantics = minValue !== null || maxValue !== null;
        const displayStats: Array<{ label: string; value: number | null }> = [];

        if (hasMinMaxSemantics) {
          if (minValue !== null) {
            displayStats.push({ label: 'Min', value: minValue });
          }
          if (maxValue !== null) {
            displayStats.push({ label: 'Max', value: maxValue });
          }
          if (avgValue !== null) {
            displayStats.push({ label: 'Avg', value: avgValue });
          }
        } else {
          if (startValue !== null) {
            displayStats.push({ label: 'Start', value: startValue });
          }
          if (endValue !== null) {
            displayStats.push({ label: 'End', value: endValue });
          }
          if (avgValue !== null) {
            displayStats.push({ label: 'Avg', value: avgValue });
          }
        }

        if (displayStats.length === 0) {
          const latestRow = this.pickMostRecentNumericRow(selectedRows);
          displayStats.push({
            label: 'Value',
            value: latestRow ? this.parseNumber(latestRow.actualResult) : null
          });
        }

        const arcLeftLabel = hasMinMaxSemantics
          ? 'Min'
          : startValue !== null
          ? 'Start'
          : '';
        const arcLeftValue = hasMinMaxSemantics ? minValue : startValue;
        const arcRightLabel = hasMinMaxSemantics
          ? 'Max'
          : endValue !== null
          ? 'End'
          : '';
        const arcRightValue = hasMinMaxSemantics ? maxValue : endValue;

        const status = this.getMetricStatus(selectedRows);
        const expectedResult = this.getExpectedResult(avgRow, selectedRows);
        const score = this.getScoreFromRange(
          rangeMinValue,
          rangeMaxValue,
          avgForScore,
          expectedResult,
          status
        );

        const latestCreatedOn = selectedRows.reduce((latestRow, currentRow) => {
          if (!latestRow) {
            return currentRow;
          }

          return this.getCreatedTime(currentRow.createdOn) >
            this.getCreatedTime(latestRow.createdOn)
            ? currentRow
            : latestRow;
        }, null as ExecutionKpiMetric | null);

        return {
          label: this.getMetricLabel(metricName),
          metricName,
          startValue,
          endValue,
          minValue,
          maxValue,
          avgValue,
          displayStats,
          arcLeftLabel,
          arcLeftValue,
          arcRightLabel,
          arcRightValue,
          expectedResult,
          unit: this.getUnit(avgRow, maxRow, minRow, selectedRows),
          sampleCount: this.getSampleCount(selectedRows),
          status,
          score,
          createdOn: latestCreatedOn ? latestCreatedOn.createdOn : ''
        };
      })
      .filter(card => card.metricName !== '');

    this.summary = this.getSummary(this.gaugeCards);
  }

  private buildTrendData() {
    // Reset chart series before rebuilding to prevent stale merged lines.
    this.trendChartOptions = {
      ...(this.trendChartOptions || {}),
      series: []
    };

    const scopedRows = this.rawMetrics.filter(
      row =>
        row.brandName === this.selectedBrandName &&
        (!this.selectedProjectName || row.projectName === this.selectedProjectName) &&
        (!this.selectedPlatformName ||
          row.platformName === this.selectedPlatformName)
    );

    this.trendMetricOptions = this.getUniqueValues(scopedRows, 'metricName');
    if (
      this.selectedTrendMetric === '' ||
      this.trendMetricOptions.indexOf(this.selectedTrendMetric) === -1
    ) {
      this.selectedTrendMetric = this.trendMetricOptions.length
        ? this.trendMetricOptions[0]
        : '';
    }

    if (!this.selectedTrendMetric) {
      this.clearTrendData();
      return;
    }

    const metricRows = scopedRows.filter(
      row => row.metricName === this.selectedTrendMetric
    );

    if (metricRows.length === 0) {
      this.clearTrendData();
      return;
    }

    const buildOrder = this.getBuildOrder(metricRows);
    this.trendCategories = buildOrder.map(buildName =>
      this.getCompactBuildLabel(buildName)
    );

    const allowedAggregations = this.getPreferredTrendAggregations(metricRows);

    const trendSeriesMap = new Map<string, TrendSeriesDefinition>();

    buildOrder.forEach((buildName, buildIndex) => {
      const buildRows = this.getLatestRowsByParameterAndAggregation(
        metricRows.filter(row => row.buildNo === buildName)
      );

      buildRows.forEach(row => {
        if (!this.shouldIncludeTrendRow(row, allowedAggregations)) {
          return;
        }

        const value = this.parseNumber(row.actualResult);
        if (value === null) {
          return;
        }

        const key = this.getTrendSeriesKey(row);
        if (!trendSeriesMap.has(key)) {
          trendSeriesMap.set(key, {
            key,
            label: this.getTrendSeriesLabel(row),
            unit: row.unit || '',
            color: this.getTrendSeriesColor(key),
            data: new Array(buildOrder.length).fill(null)
          });
        }

        trendSeriesMap.get(key).data[buildIndex] = value;
      });
    });

    this.trendSeries = Array.from(trendSeriesMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    this.trendTableRows = this.trendSeries.map(series => {
      const values = series.data.filter(v => v !== null) as number[];
      const latest = this.getLastNonNullValue(series.data);
      const min = values.length > 0 ? Math.min(...values) : null;
      const max = values.length > 0 ? Math.max(...values) : null;

      return {
        parameter: series.label,
        latest: this.formatMetricValue(latest) + (series.unit ? ` ${series.unit}` : ''),
        min: this.formatMetricValue(min) + (series.unit ? ` ${series.unit}` : ''),
        max: this.formatMetricValue(max) + (series.unit ? ` ${series.unit}` : '')
      };
    });

    this.trendChartOptions = {
      chart: {
        type: 'line',
        height: 280,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: this.trendCategories,
        title: {
          text: 'Build'
        }
      },
      yAxis: {
        title: {
          text: 'Metric Value'
        }
      },
      tooltip: {
        shared: true,
        valueDecimals: 3
      },
      legend: {
        enabled: true
      },
      series: this.trendSeries.map(series => ({
        type: 'line',
        name: series.label,
        color: series.color,
        data: series.data,
        connectNulls: false
      }))
    };

    this.updateTrendChart = !this.updateTrendChart;
  }

  private getPreferredTrendAggregations(rows: ExecutionKpiMetric[]): string[] {
    const aggregations = Array.from(
      new Set(rows.map(row => this.normalizeToken(row.aggregationType)))
    );

    const hasAvg = aggregations.indexOf('AVG') > -1 || aggregations.indexOf('MEAN') > -1;
    const hasFirst = aggregations.indexOf('FIRST') > -1;
    const hasLast = aggregations.indexOf('LAST') > -1;
    const hasMin = aggregations.indexOf('MIN') > -1;
    const hasMax = aggregations.indexOf('MAX') > -1;

    // For response types like app launch/screen time, keep trend focused on AVG only.
    if (hasAvg) {
      return ['AVG', 'MEAN'];
    }

    // For CPU/Memory patterns, show full envelope: FIRST/LAST/MAX (and MIN if present).
    if (hasMax && (hasFirst || hasLast)) {
      const envelope: string[] = [];
      if (hasFirst) {
        envelope.push('FIRST');
      }
      if (hasLast) {
        envelope.push('LAST');
      }
      envelope.push('MAX');
      if (hasMin) {
        envelope.push('MIN');
      }
      return envelope;
    }

    if (hasMin || hasMax) {
      return ['MIN', 'MAX'];
    }

    if (hasFirst || hasLast) {
      return ['FIRST', 'LAST'];
    }

    return [];
  }

  private shouldIncludeTrendRow(
    row: ExecutionKpiMetric,
    allowedAggregations: string[]
  ): boolean {
    if (allowedAggregations.length === 0) {
      return true;
    }

    const aggregation = this.normalizeToken(row.aggregationType);
    return allowedAggregations.indexOf(aggregation) > -1;
  }

  private normalizeToken(value: string): string {
    return (value || '').trim().toUpperCase();
  }

  private clearTrendData() {
    this.trendCategories = [];
    this.trendSeries = [];
    this.trendTableRows = [];
    this.trendChartOptions = {};
    this.updateTrendChart = !this.updateTrendChart;
  }

  private getBuildOrder(rows: ExecutionKpiMetric[]): string[] {
    const latestDateByBuild: { [buildNo: string]: number } = {};

    rows.forEach(row => {
      const buildNo = row.buildNo || '';
      if (!buildNo) {
        return;
      }

      const created = this.getCreatedTime(row.createdOn);
      if (
        latestDateByBuild[buildNo] === undefined ||
        created > latestDateByBuild[buildNo]
      ) {
        latestDateByBuild[buildNo] = created;
      }
    });

    return Object.keys(latestDateByBuild).sort(
      (left, right) => latestDateByBuild[left] - latestDateByBuild[right]
    );
  }

  private getTrendSeriesKey(row: ExecutionKpiMetric): string {
    return `${(row.aggregationType || 'NA').toUpperCase()}__${
      (row.metricParameter || 'NA').toUpperCase()
    }`;
  }

  private getTrendSeriesLabel(row: ExecutionKpiMetric): string {
    const aggregation = (row.aggregationType || 'NA').toUpperCase();
    const parameter = (row.metricParameter || 'NA').toUpperCase();

    if (parameter === 'NA' || parameter === 'N/A' || parameter === '') {
      return aggregation;
    }

    return `${aggregation} (${parameter})`;
  }

  private getTrendSeriesColor(seriesKey: string): string {
    const aggregation = (seriesKey.split('__')[0] || '').toUpperCase();

    if (aggregation === 'FIRST') {
      return '#0f4fa8';
    }

    if (aggregation === 'LAST') {
      return '#db3b21';
    }

    if (aggregation === 'MAX') {
      return '#f2a900';
    }

    if (aggregation === 'MIN') {
      return '#1aaa55';
    }

    if (aggregation === 'AVG' || aggregation === 'MEAN') {
      return '#00897b';
    }

    return '#6b7280';
  }

  private getCompactBuildLabel(buildNo: string): string {
    if (!buildNo) {
      return '';
    }

    const compact = buildNo.length > 26 ? `${buildNo.slice(0, 23)}...` : buildNo;
    return compact;
  }

  private getLastNonNullValue(values: Array<number | null>): number | null {
    for (let index = values.length - 1; index >= 0; index -= 1) {
      if (values[index] !== null) {
        return values[index];
      }
    }

    return null;
  }

  private getMetricLabel(metricName: string): string {
    return this.formatMetricToken(metricName);
  }

  private formatMetricToken(value: string): string {
    return (value || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, letter => letter.toUpperCase());
  }

  private getScoreFromRange(
    minValue: number | null,
    maxValue: number | null,
    avgValue: number | null,
    expectedResult: string,
    status: string
  ): number {
    if (
      minValue !== null &&
      maxValue !== null &&
      avgValue !== null &&
      maxValue > minValue
    ) {
      const rangeScore = ((avgValue - minValue) / (maxValue - minValue)) * 100;
      return this.clamp(Math.round(rangeScore), 0, 100);
    }

    if (avgValue !== null && maxValue !== null && maxValue > 0) {
      return this.clamp(Math.round((avgValue / maxValue) * 100), 0, 100);
    }

    const expected = this.parseNumber(expectedResult);
    const actual = avgValue;

    if (expected !== null && expected > 0 && actual !== null) {
      const ratio = (expected / actual) * 100;
      return this.clamp(Math.round(ratio), 0, 100);
    }

    if (status === 'PASS') {
      return 100;
    }

    if (status === 'FAIL') {
      return 35;
    }

    return 60;
  }

  getGaugeResultText(card: KpiGaugeCard): string {
    const valueSource =
      card.avgValue !== null
        ? card.avgValue
        : card.endValue !== null
        ? card.endValue
        : card.startValue !== null
        ? card.startValue
        : card.maxValue !== null
        ? card.maxValue
        : card.minValue;

    const value = this.formatMetricValue(valueSource);
    if (value === 'N/A') {
      return value;
    }

    return card.unit ? `${value} ${card.unit}` : value;
  }

  getGaugeArcLength(score: number): number {
    const normalizedScore = this.clamp(score, 0, 100);
    return (normalizedScore / 100) * this.gaugeSemiCircumference;
  }

  getGaugeMarkerX(score: number): number {
    const normalizedScore = this.clamp(score, 0, 100);
    const angleDeg = 180 - (180 * normalizedScore) / 100;
    const angleRad = (angleDeg * Math.PI) / 180;
    const centerX = 70;
    return centerX + this.gaugeRadius * Math.cos(angleRad);
  }

  getGaugeMarkerY(score: number): number {
    const normalizedScore = this.clamp(score, 0, 100);
    const angleDeg = 180 - (180 * normalizedScore) / 100;
    const angleRad = (angleDeg * Math.PI) / 180;
    const centerY = 70;
    return centerY - this.gaugeRadius * Math.sin(angleRad);
  }

  getGaugeTooltip(card: KpiGaugeCard): string {
    return card.displayStats
      .map(stat => `${stat.label}: ${this.formatMetricValue(stat.value)} ${card.unit}`)
      .join(' | ');
  }

  private getLatestRowsByParameterAndAggregation(
    rows: ExecutionKpiMetric[]
  ): ExecutionKpiMetric[] {
    const latestMap = new Map<string, ExecutionKpiMetric>();

    rows.forEach(row => {
      const key = `${(row.metricParameter || '').toUpperCase()}__${
        (row.aggregationType || '').toUpperCase()
      }`;
      const current = latestMap.get(key);

      if (!current) {
        latestMap.set(key, row);
        return;
      }

      if (this.getCreatedTime(row.createdOn) > this.getCreatedTime(current.createdOn)) {
        latestMap.set(key, row);
      }
    });

    return Array.from(latestMap.values());
  }

  private pickRowByAggregations(
    rows: ExecutionKpiMetric[],
    aggregationTypes: string[]
  ): ExecutionKpiMetric | null {
    for (const aggregationType of aggregationTypes) {
      const match = rows.find(
        row =>
          (row.aggregationType || '').toUpperCase() ===
            aggregationType.toUpperCase() &&
          this.parseNumber(row.actualResult) !== null
      );

      if (match) {
        return match;
      }
    }

    return null;
  }

  private pickRowByParameters(
    rows: ExecutionKpiMetric[],
    metricParameters: string[]
  ): ExecutionKpiMetric | null {
    for (const metricParameter of metricParameters) {
      const match = rows.find(
        row =>
          (row.metricParameter || '').toUpperCase() ===
            metricParameter.toUpperCase() &&
          this.parseNumber(row.actualResult) !== null
      );

      if (match) {
        return match;
      }
    }

    return null;
  }

  private pickLowestNumericRow(rows: ExecutionKpiMetric[]): ExecutionKpiMetric | null {
    let selectedRow: ExecutionKpiMetric | null = null;
    let selectedValue = Number.POSITIVE_INFINITY;

    rows.forEach(row => {
      const value = this.parseNumber(row.actualResult);
      if (value !== null && value < selectedValue) {
        selectedValue = value;
        selectedRow = row;
      }
    });

    return selectedRow;
  }

  private pickHighestNumericRow(rows: ExecutionKpiMetric[]): ExecutionKpiMetric | null {
    let selectedRow: ExecutionKpiMetric | null = null;
    let selectedValue = Number.NEGATIVE_INFINITY;

    rows.forEach(row => {
      const value = this.parseNumber(row.actualResult);
      if (value !== null && value > selectedValue) {
        selectedValue = value;
        selectedRow = row;
      }
    });

    return selectedRow;
  }

  private pickMostRecentNumericRow(rows: ExecutionKpiMetric[]): ExecutionKpiMetric | null {
    let selectedRow: ExecutionKpiMetric | null = null;
    let selectedTime = 0;

    rows.forEach(row => {
      const value = this.parseNumber(row.actualResult);
      const createdTime = this.getCreatedTime(row.createdOn);

      if (value !== null && createdTime >= selectedTime) {
        selectedTime = createdTime;
        selectedRow = row;
      }
    });

    return selectedRow;
  }

  private pickPreferredAvgRow(rows: ExecutionKpiMetric[]): ExecutionKpiMetric | null {
    const avgRows = rows.filter(
      row =>
        ['AVG', 'MEAN'].indexOf((row.aggregationType || '').toUpperCase()) > -1 &&
        this.parseNumber(row.actualResult) !== null
    );

    if (avgRows.length === 0) {
      return null;
    }

    const preferredNaRow = avgRows.find(row => {
      const parameter = (row.metricParameter || '').toUpperCase();
      return parameter === 'NA' || parameter === 'N/A' || parameter === '';
    });

    if (preferredNaRow) {
      return preferredNaRow;
    }

    return avgRows.sort(
      (first, second) =>
        this.getCreatedTime(second.createdOn) - this.getCreatedTime(first.createdOn)
    )[0];
  }

  private getNumericRows(rows: ExecutionKpiMetric[]): ExecutionKpiMetric[] {
    return rows.filter(row => this.parseNumber(row.actualResult) !== null);
  }

  private getMinValueFromRows(rows: ExecutionKpiMetric[]): number | null {
    if (rows.length === 0) {
      return null;
    }

    return rows.reduce((minValue, row) => {
      const value = this.parseNumber(row.actualResult);
      if (value === null) {
        return minValue;
      }

      return minValue === null || value < minValue ? value : minValue;
    }, null as number | null);
  }

  private getMaxValueFromRows(rows: ExecutionKpiMetric[]): number | null {
    if (rows.length === 0) {
      return null;
    }

    return rows.reduce((maxValue, row) => {
      const value = this.parseNumber(row.actualResult);
      if (value === null) {
        return maxValue;
      }

      return maxValue === null || value > maxValue ? value : maxValue;
    }, null as number | null);
  }

  private getAverageValueFromRows(rows: ExecutionKpiMetric[]): number | null {
    if (rows.length === 0) {
      return null;
    }

    const values = rows
      .map(row => this.parseNumber(row.actualResult))
      .filter(value => value !== null) as number[];

    if (values.length === 0) {
      return null;
    }

    const sum = values.reduce((total, value) => total + value, 0);
    return sum / values.length;
  }

  private getMetricStatus(rows: ExecutionKpiMetric[]): string {
    const statuses = rows.map(row => (row.status || '').toUpperCase());
    if (statuses.indexOf('FAIL') > -1) {
      return 'FAIL';
    }

    if (statuses.indexOf('PASS') > -1) {
      return 'PASS';
    }

    return 'UNKNOWN';
  }

  private getExpectedResult(
    avgRow: ExecutionKpiMetric | null,
    rows: ExecutionKpiMetric[]
  ): string {
    if (avgRow && avgRow.expectedResult && avgRow.expectedResult !== 'N/A') {
      return avgRow.expectedResult;
    }

    const withExpected = rows.find(
      row => row.expectedResult && row.expectedResult.trim().toUpperCase() !== 'N/A'
    );

    return withExpected ? withExpected.expectedResult : 'N/A';
  }

  private getUnit(
    avgRow: ExecutionKpiMetric | null,
    maxRow: ExecutionKpiMetric | null,
    minRow: ExecutionKpiMetric | null,
    rows: ExecutionKpiMetric[]
  ): string {
    if (avgRow && avgRow.unit) {
      return avgRow.unit;
    }

    if (maxRow && maxRow.unit) {
      return maxRow.unit;
    }

    if (minRow && minRow.unit) {
      return minRow.unit;
    }

    const rowWithUnit = rows.find(row => !!row.unit);
    return rowWithUnit ? rowWithUnit.unit : '';
  }

  private getSampleCount(rows: ExecutionKpiMetric[]): number {
    return rows.reduce((total, row) => total + Number(row.sampleCount || 0), 0);
  }

  formatMetricValue(value: number | null): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    const rounded = Math.round(value * 1000) / 1000;
    return `${rounded}`;
  }

  private getSummary(cards: KpiGaugeCard[]) {
    const pass = cards.filter(card => card.status === 'PASS').length;
    const fail = cards.filter(card => card.status === 'FAIL').length;
    const total = cards.length;

    const latestTime = cards.reduce((currentLatest, card) => {
      const cardTime = this.getCreatedTime(card.createdOn);
      return cardTime > currentLatest ? cardTime : currentLatest;
    }, 0);

    return {
      total,
      pass,
      fail,
      unknown: total - pass - fail,
      lastUpdated: latestTime > 0 ? this.formatCreatedOn(latestTime) : ''
    };
  }

  private getUniqueValues<T>(rows: T[], key: keyof T): string[] {
    const values = rows
      .map(item => (item[key] as unknown as string) || '')
      .filter(value => value && value.trim() !== '');
    return Array.from(new Set(values)).sort((first, second) =>
      first.localeCompare(second)
    );
  }

  private getCreatedTime(value: string): number {
    if (!value) {
      return 0;
    }

    const normalized = value.replace(' ', 'T');
    const time = new Date(normalized).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private parseNumber(value: string): number | null {
    if (!value) {
      return null;
    }

    const sanitized = `${value}`.replace(/,/g, '').trim();
    if (sanitized.toUpperCase() === 'N/A') {
      return null;
    }

    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private formatCreatedOn(time: number): string {
    const date = new Date(time);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hour = `${date.getHours()}`.padStart(2, '0');
    const minute = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute}`;
  }
}
